'use client';

import { type ReactNode, useEffect, useState } from 'react';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { Icon } from '@/app_components/common/Icon';
import * as Popover from '@/app_components/ui/primitives/Popover';
import { ContentPlacement } from '@/app_lib/types/analytics';
import { cn } from '@/app_lib/utils/utils';

export const BasicPopover = ({
  className,
  trigger,
  content,
  contentClassName = 'z-header-popover',
  align = 'start',
  side = 'bottom',
  closeClassName,
  canClose = false,
  shouldCloseOnScroll = true,
  shouldCloseOnMouseLeave = true,
  isShareButtonHovered = false,
}: {
  className?: string;
  closeClassName?: string;
  contentClassName?: string;
  trigger: ReactNode;
  content: ReactNode;
  canClose?: boolean;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  shouldCloseOnScroll?: boolean;
  shouldCloseOnMouseLeave?: boolean;
  isShareButtonHovered?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // NOTE: This will run for all popover's on every scroll event
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen || isShareButtonHovered) {
        setIsOpen(false);
      }
    };

    if (shouldCloseOnScroll) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen, shouldCloseOnScroll, isShareButtonHovered]);

  return (
    // biome-ignore lint/a11y/useSemanticElements: need to check with platform team
    // biome-ignore lint/a11y/useFocusableInteractive: need to check with platform team
    <div
      className={className}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => { if (shouldCloseOnMouseLeave) {setIsOpen(false); }}}
      role='button'
    >
      <Popover.Root
        modal={false}
        onOpenChange={setIsOpen}
        open={isOpen || isShareButtonHovered}
      >
        <Popover.Trigger
          asChild
          tabIndex={0}
        >
          {trigger}
        </Popover.Trigger>
        <Popover.Content
          align={align}
          avoidCollisions={true}
          className={contentClassName}
          collisionPadding={8}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onOpenAutoFocus={(e) => e.preventDefault()}
          side={side}
        >
          <div className='relative'>
            {content}
            {canClose && (
              <Popover.Close
                asChild
                className={cn('absolute top-2 right-2 cursor-pointer', closeClassName)}
                data-cy={ContentPlacement.CloseButton}
              >
                <ActionableIcon>
                  <Icon
                    className='hover:icon-primary-hover'
                    icon='X'
                  />
                </ActionableIcon>
              </Popover.Close>
            )}
          </div>
        </Popover.Content>
      </Popover.Root>
    </div>
  );
};
